import React from 'react';
import { Link } from 'react-router-dom';
import Header from './common/Header';
import Footer from './common/Footer';
import MetaTags from './SEO/MetaTags';
import SocialShare from './SEO/SocialShare';
import { generatePageSEO } from '../utils/SEOUtils';

const AboutPage = () => {
  const seoData = generatePageSEO('about');
  
  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <MetaTags {...seoData} />
      <Header />
      
      <main className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold mb-6 bg-gradient-to-r from-teal-400 to-green-400 bg-clip-text text-transparent">
            About ChillFree TV
          </h1>
          <p className="text-xl text-gray-300 leading-relaxed">
            Where classic entertainment meets cutting-edge technology
          </p>
        </div>

        {/* Mission Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-6 text-teal-400">Our Mission</h2>
          <div className="prose prose-lg prose-invert max-w-none">
            <p className="text-gray-300 leading-relaxed mb-6">
              Welcome to ChillFree TV, founded by entertainment professionals who are passionate lovers of movies and TV shows. Our mission is to bring you a carefully curated selection of classic movies, timeless TV shows, and so much more – all completely free.
            </p>
            <p className="text-gray-300 leading-relaxed">
              ChillFree TV Network is a free streaming platform that aims to transport you back in time to the golden era of cinema and television. With our vast library of classic content, we offer a unique opportunity to relive the magic of iconic films and beloved series that shaped entertainment history.
            </p>
          </div>
        </section>

        {/* What Makes Us Special */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-teal-400">What Makes ChillFree TV Special</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-xl font-semibold mb-4 text-green-400">🎬 Completely Free</h3>
              <p className="text-gray-300">
                Enjoy our entire library without subscription fees or hidden costs. Quality entertainment should be accessible to everyone.
              </p>
            </div>
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-xl font-semibold mb-4 text-green-400">✨ Curated Selection</h3>
              <p className="text-gray-300">
                Every title is handpicked by entertainment professionals to ensure you get the best of classic cinema and television.
              </p>
            </div>
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-xl font-semibold mb-4 text-green-400">📺 High-Quality Streaming</h3>
              <p className="text-gray-300">
                Experience your favorite classics in crisp, high-quality streams that do justice to these timeless productions.
              </p>
            </div>
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-xl font-semibold mb-4 text-green-400">🌐 Watch Anywhere</h3>
              <p className="text-gray-300">
                Compatible with all your devices – laptop, tablet, smartphone, or smart TV. Your entertainment travels with you.
              </p>
            </div>
          </div>
        </section>

        {/* AI Technology Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-teal-400">Built with AI Innovation</h2>
          <div className="bg-gradient-to-r from-gray-800 to-gray-700 rounded-lg p-8 border border-teal-500/30">
            <div className="flex items-start space-x-4 mb-6">
              <div className="w-12 h-12 bg-gradient-to-r from-teal-400 to-green-400 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-xl">🤖</span>
              </div>
              <div>
                <h3 className="text-2xl font-semibold mb-4 text-white">Powered by Manus AI</h3>
                <p className="text-gray-300 leading-relaxed mb-4">
                  ChillFree TV represents a groundbreaking collaboration between human creativity and artificial intelligence. Our platform was developed with the assistance of Manus AI, an advanced AI agent that helped architect and build many of the sophisticated features you experience today.
                </p>
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6 mt-8">
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-teal-300">🎯 Smart User Experience</h4>
                <p className="text-gray-300 text-sm">
                  AI-powered interface design ensures intuitive navigation, responsive layouts, and seamless interactions across all devices.
                </p>
              </div>
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-teal-300">⚡ Intelligent Performance</h4>
                <p className="text-gray-300 text-sm">
                  Advanced optimization algorithms ensure lightning-fast loading times and smooth streaming experiences.
                </p>
              </div>
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-teal-300">🔍 Content Discovery</h4>
                <p className="text-gray-300 text-sm">
                  AI-enhanced categorization and recommendation systems help you discover hidden gems from our classic collection.
                </p>
              </div>
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-teal-300">🛡️ Quality Assurance</h4>
                <p className="text-gray-300 text-sm">
                  Automated testing and quality control systems ensure consistent, reliable performance across the platform.
                </p>
              </div>
            </div>
            
            <div className="mt-8 p-6 bg-gray-900/50 rounded-lg border border-teal-500/20">
              <p className="text-gray-300 italic text-center">
                "The future of entertainment platforms lies in the seamless integration of human creativity and AI innovation. ChillFree TV is a testament to what's possible when technology serves storytelling."
              </p>
              <p className="text-teal-400 text-center mt-2 font-semibold">— The ChillFree TV Development Team</p>
            </div>
          </div>
        </section>

        {/* Our Story */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-6 text-teal-400">Our Story</h2>
          <div className="prose prose-lg prose-invert max-w-none">
            <p className="text-gray-300 leading-relaxed mb-6">
              ChillFree TV was born from a simple belief: everyone deserves access to quality entertainment. Our founders, seasoned entertainment industry professionals, recognized that while streaming services were proliferating, there was a gap in the market for classic, timeless content that didn't require expensive subscriptions.
            </p>
            <p className="text-gray-300 leading-relaxed mb-6">
              We set out to create more than just another streaming platform. We wanted to build a digital time machine that could transport viewers back to the golden age of cinema and television, when storytelling was king and every production was crafted with care and attention to detail.
            </p>
            <p className="text-gray-300 leading-relaxed">
              Today, ChillFree TV stands as a bridge between the past and the future – preserving classic entertainment while leveraging cutting-edge technology to deliver it in new and exciting ways. We're not just streaming content; we're preserving cultural heritage and making it accessible to new generations of viewers.
            </p>
          </div>
        </section>

        {/* Technology & Innovation */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-6 text-teal-400">Technology & Innovation</h2>
          <div className="bg-gray-800 rounded-lg p-8 border border-gray-700">
            <p className="text-gray-300 leading-relaxed mb-6">
              Behind ChillFree TV's sleek interface lies sophisticated technology designed to deliver the best possible viewing experience. Our platform utilizes advanced streaming protocols, adaptive bitrate technology, and intelligent content delivery networks to ensure smooth playback regardless of your internet connection.
            </p>
            <div className="grid md:grid-cols-3 gap-6 mt-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-teal-400 to-green-400 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">⚡</span>
                </div>
                <h4 className="font-semibold mb-2">Lightning Fast</h4>
                <p className="text-gray-400 text-sm">Optimized for instant loading and seamless playback</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-teal-400 to-green-400 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🔒</span>
                </div>
                <h4 className="font-semibold mb-2">Secure & Private</h4>
                <p className="text-gray-400 text-sm">Your data and viewing habits are protected</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-teal-400 to-green-400 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">📱</span>
                </div>
                <h4 className="font-semibold mb-2">Cross-Platform</h4>
                <p className="text-gray-400 text-sm">Works seamlessly across all your devices</p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="text-center">
          <h2 className="text-3xl font-bold mb-6 text-teal-400">Get in Touch</h2>
          <p className="text-gray-300 mb-8 leading-relaxed">
            Have questions, suggestions, or just want to share your love for classic entertainment? We'd love to hear from you.
          </p>
          <Link 
            to="/contact" 
            className="inline-block bg-gradient-to-r from-teal-500 to-green-500 text-white px-8 py-3 rounded-lg font-semibold hover:from-teal-600 hover:to-green-600 transition-all duration-300 transform hover:scale-105"
          >
            Contact Us
          </Link>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AboutPage;

